import { ExamController } from './exam';
import { QuizOverlay } from './quiz/overlay';
import { showXpPopup } from './quiz/feedback';
import { generateMul2x1 } from './generators/math/mul2x1';
import { loadChineseZhuyinBank, sampleBank } from './content/chinese-loader';
import { loadProgress, saveProgress, createFreshProgress, addFactionXP, recordQuestionAnswered } from '../shared/progress';
import { mountEscapeButton } from './escape-button';
import type { GeneratedQuestion } from './generators/math/types';
import type { Subject } from '../shared/types';
import { renderPaperView } from './exam/paper-view';
import { composeChapterQuiz } from './exam/mock-paper-composer';
import { FACTIONS, factionForSubject } from '../shared/factions';

const TIME_LIMIT_SEC = 600; // 10 min generous

export async function runExam(container: HTMLElement, onDone: () => void): Promise<void> {
  // Build mixed question set
  const mathQs: GeneratedQuestion[] = [];
  for (let i = 0; i < 5; i++) mathQs.push(generateMul2x1(Date.now() + i * 17, ((i % 3) + 1) as 1 | 2 | 3));
  const bank = await loadChineseZhuyinBank();
  const chineseQs = sampleBank(bank, 5);
  const allQs = shuffle([...mathQs, ...chineseQs]);

  const exam = new ExamController(allQs, TIME_LIMIT_SEC, 'practice');

  const root = document.createElement('div');
  root.className = 'exam-screen';
  container.appendChild(root);

  // Timer HUD
  const timerEl = document.createElement('div');
  timerEl.className = 'exam-timer';
  root.appendChild(timerEl);

  const overlayHost = document.createElement('div');
  root.appendChild(overlayHost);

  const overlay = new QuizOverlay({
    container: overlayHost,
    onAnswer: (answer: number | string) => {
      const result = exam.submit(answer);
      showXpPopup(result.correct ? '+24 ✓' : '✗', result.correct ? '#80cbc4' : '#ef5350');
      if (result.done) {
        setTimeout(finish, 500);
      } else {
        setTimeout(render, 500);
      }
    },
  });

  // Skip button for practice mode
  const skipBtn = document.createElement('button');
  skipBtn.className = 'secondary-btn';
  skipBtn.textContent = '跳過';
  skipBtn.style.marginTop = '12px';
  skipBtn.addEventListener('click', () => {
    const result = exam.skip();
    if (result.done) setTimeout(finish, 200); else setTimeout(render, 200);
  });
  root.appendChild(skipBtn);

  const unmountEscape = mountEscapeButton(() => {
    clearInterval(timerInterval);
    overlay.destroy();
    unmountEscape();
    root.remove();
    onDone();
  });

  function render(): void {
    overlay.render(exam.currentQuestion(), 'challenge', exam.position());
    timerEl.textContent = `剩餘 ${Math.ceil(exam.remainingSec())} 秒  |  ${exam.position().current} / ${exam.position().total}`;
  }

  // Timer tick
  const timerInterval = window.setInterval(() => {
    timerEl.textContent = `剩餘 ${Math.ceil(exam.remainingSec())} 秒  |  ${exam.position().current} / ${exam.position().total}`;
  }, 1000);

  function finish(): void {
    unmountEscape();
    clearInterval(timerInterval);
    overlay.destroy();
    const result = exam.finalize();
    // Persist XP — split half to math, half to chinese
    let progress = loadProgress();
    if (!progress) progress = createFreshProgress('指揮官');
    addFactionXP(progress, 'celestial', Math.round(result.totalXP / 2));
    addFactionXP(progress, 'runic', Math.round(result.totalXP / 2));
    // C06 parent dashboard — bulk-bump today's question count by however
    // many questions the kid actually attempted (skipped questions still
    // count as faced for "answered today" metrics).
    recordQuestionAnswered(progress, result.totalQuestions);
    saveProgress(progress);
    root.innerHTML = `
      <h1>考試結束</h1>
      <p>對 ${result.correctCount} / ${result.totalQuestions} 題</p>
      <p>獲得 ${Math.round(result.totalXP)} 修煉值 (星算門 + 符文門 平分)</p>
      <button class="primary-btn" id="exam-back">回主畫面</button>
    `;
    root.querySelector('#exam-back')!.addEventListener('click', () => { root.remove(); onDone(); });
  }

  render();
}

// ─── Chapter Quiz entry point ─────────────────────────────────────────────────

export interface RunChapterQuizOpts {
  container: HTMLElement;
  subject: Subject;
  chapterNumber: number;
  count?: 10 | 15;
  rngSeed?: number;
  onDone: () => void;
}

/**
 * runChapterQuiz
 *
 * Entry point for 章節練習卷 (paper-mode chapter exam).
 * Composes questions via composeChapterQuiz, then renders PaperView.
 */
export async function runChapterQuiz(opts: RunChapterQuizOpts): Promise<void> {
  const { container, subject, chapterNumber, count = 10, rngSeed, onDone } = opts;

  // Loading screen
  const loading = document.createElement('div');
  loading.className = 'pv-loading formal-page';
  loading.textContent = '正在組卷，請稍候…';
  container.appendChild(loading);

  let questions: GeneratedQuestion[];
  try {
    questions = await composeChapterQuiz({ subject, chapterNumber, count, rngSeed });
  } catch (e) {
    loading.textContent = `組卷失敗：${String(e)}`;
    const back = document.createElement('button');
    back.className = 'primary-btn';
    back.style.marginTop = '16px';
    back.textContent = '返回';
    back.addEventListener('click', () => { loading.remove(); onDone(); });
    loading.appendChild(back);
    return;
  }
  loading.remove();

  if (questions.length === 0) {
    const err = document.createElement('div');
    err.className = 'pv-loading formal-page';
    err.innerHTML = '<p>目前沒有可用的題目（該章節 ready KP 為空）。</p>';
    const back = document.createElement('button');
    back.className = 'primary-btn';
    back.textContent = '返回';
    back.addEventListener('click', () => { err.remove(); onDone(); });
    err.appendChild(back);
    container.appendChild(err);
    return;
  }

  const factionInfo = FACTIONS[factionForSubject(subject)];
  const title = `${factionInfo.emoji} ${factionInfo.displayName} 第 ${chapterNumber} 章練習卷`;

  renderPaperView({
    container,
    questions,
    subject,
    timeLimitSec: 900,
    title,
    onDone,
  });
}

/**
 * runExamWithQuestions
 *
 * Flash-practice entry point for retrying wrong questions (from paper-view result page).
 * Reuses the legacy QuizOverlay / ExamController for immediate feedback.
 */
export async function runExamWithQuestions(
  container: HTMLElement,
  questions: GeneratedQuestion[],
  onDone: () => void,
): Promise<void> {
  if (questions.length === 0) { onDone(); return; }

  const exam = new ExamController(questions, 300, 'practice');

  const root = document.createElement('div');
  root.className = 'exam-screen';
  container.appendChild(root);

  const timerEl = document.createElement('div');
  timerEl.className = 'exam-timer';
  root.appendChild(timerEl);

  const overlayHost = document.createElement('div');
  root.appendChild(overlayHost);

  const overlay = new QuizOverlay({
    container: overlayHost,
    onAnswer: () => {
      if (exam.done) {
        setTimeout(finish, 500);
      } else {
        setTimeout(render, 500);
      }
    },
  });

  const skipBtn = document.createElement('button');
  skipBtn.className = 'secondary-btn';
  skipBtn.textContent = '跳過';
  skipBtn.style.marginTop = '12px';
  skipBtn.addEventListener('click', () => {
    const result = exam.skip();
    if (result.done) setTimeout(finish, 200); else setTimeout(render, 200);
  });
  root.appendChild(skipBtn);

  const unmountEscape = mountEscapeButton(() => {
    clearInterval(timerInterval);
    overlay.destroy();
    unmountEscape();
    root.remove();
    onDone();
  });

  function render(): void {
    overlay.render(exam.currentQuestion(), 'challenge', exam.position());
    timerEl.textContent = `剩餘 ${Math.ceil(exam.remainingSec())} 秒  |  ${exam.position().current} / ${exam.position().total}`;
  }

  const timerInterval = window.setInterval(() => {
    timerEl.textContent = `剩餘 ${Math.ceil(exam.remainingSec())} 秒  |  ${exam.position().current} / ${exam.position().total}`;
  }, 1000);

  function finish(): void {
    unmountEscape();
    clearInterval(timerInterval);
    overlay.destroy();
    const result = exam.finalize();
    root.innerHTML = `
      <h1>錯題練習結束</h1>
      <p>對 ${result.correctCount} / ${result.totalQuestions} 題</p>
      <button class="primary-btn" id="exam-retry-back">回主畫面</button>
    `;
    root.querySelector('#exam-retry-back')!.addEventListener('click', () => { root.remove(); onDone(); });
  }

  render();
}

function shuffle<T>(arr: T[]): T[] {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}
